/**
 * Top Menu Bar - Microsoft Word style navigation
 * Replaces the left sidebar with dropdown menus grouped by category.
 */

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  LayoutDashboard, Users, Shield, ShieldAlert, Calculator, Zap, CalendarDays,
  FileText, Scale, GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, MessageSquare, Library, Link2, Settings, HelpCircle,
  BarChart3, Sparkles, Building2, Activity, CheckCircle, CreditCard, DollarSign,
  Network, Cpu, Upload, Database, Share2, Microscope, Globe, ClipboardList, Search, Tag,
  FolderOpen, Clock, Image, ChevronDown, Star, Menu, Layers,
} from 'lucide-react';
import logoImg from '@/assets/img/logo.png';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

interface MenuItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  minPlan?: number;
  description?: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

interface MenuGroup {
  title: string;
  sections: MenuSection[];
  columns?: number; // mega menu: 2 or 3 columns side by side
}

// ==================== Menu Structure ====================

const MENU_GROUPS: MenuGroup[] = [
  {
    title: 'Clients',
    sections: [
      {
        label: 'Management',
        items: [
          { title: 'All Clients', href: '/panel/files/clients', icon: Users, description: 'Manage client portfolio' },
          { title: 'Deadlines', href: '/panel/deadlines', icon: CalendarDays, minPlan: 2, description: 'ATO lodgement tracker' },
          { title: 'Health Scores', href: '/panel/firm/dashboard', icon: Activity, minPlan: 2, description: 'Client health overview' },
        ],
      },
    ],
  },
  {
    title: 'Tax & Compliance',
    columns: 2,
    sections: [
      {
        label: 'Compliance Checks',
        items: [
          { title: 'Compliance Checker', href: '/panel/compliance/checker', icon: Shield, minPlan: 2, description: 'ATO benchmarks' },
          { title: 'Audit Risk', href: '/panel/compliance/audit-risk', icon: ShieldAlert, minPlan: 3, description: 'Audit risk predictor' },
          { title: 'BAS Validator', href: '/panel/compliance/bas', icon: FileCheck, minPlan: 2, description: 'Validate BAS labels' },
          { title: 'Div 7A Checker', href: '/panel/compliance/div7a', icon: Building2, minPlan: 2, description: 'Loans to associates' },
          { title: 'Super Compliance', href: '/panel/compliance/super', icon: Shield, minPlan: 3, description: 'SG tracking' },
          { title: 'FBT Detector', href: '/panel/compliance/fbt', icon: AlertOctagon, minPlan: 3, description: 'FBT triggers' },
        ],
      },
      {
        label: 'Tax Planning',
        items: [
          { title: 'Tax Planning', href: '/panel/compliance/tax-planning', icon: CalendarDays, minPlan: 3, description: 'Year-end strategies' },
          { title: 'Tax Opportunities', href: '/panel/compliance/opportunities', icon: Zap, minPlan: 3, description: 'Find deductions' },
          { title: 'Trust Distribution', href: '/panel/compliance/trust-distribution', icon: Scale, minPlan: 3, description: 'Distribution planner' },
          { title: 'CGT Calculator', href: '/panel/compliance/cgt', icon: Calculator, minPlan: 3, description: 'Capital gains tax' },
          { title: 'Missing Docs', href: '/panel/compliance/missing-documents', icon: FileSearch, minPlan: 2, description: 'Document checklist' },
        ],
      },
    ],
  },
  {
    title: 'Financial Analysis',
    sections: [
      {
        label: 'Analysis Tools',
        items: [
          { title: 'Reconciliation', href: '/panel/intelligence/reconciliation', icon: Scale, minPlan: 2, description: 'TB vs P&L vs BS' },
          { title: 'Smart Comparison', href: '/panel/intelligence/comparison', icon: GitCompare, minPlan: 2, description: 'Year-over-year' },
          { title: 'Cash Flow', href: '/panel/compliance/cashflow', icon: BarChart3, minPlan: 3, description: 'Cash flow forecast' },
          { title: 'Risk Warning', href: '/panel/intelligence/failure-warning', icon: AlertOctagon, minPlan: 3, description: 'Business failure alert' },
          { title: 'What-If Scenarios', href: '/panel/compliance/scenarios', icon: Calculator, minPlan: 3, description: 'Scenario modelling' },
        ],
      },
    ],
  },
  {
    title: 'Reports',
    sections: [
      {
        label: 'Generate Reports',
        items: [
          { title: 'Client Reports', href: '/panel/reports', icon: FileText, minPlan: 2, description: '8 report templates' },
        ],
      },
    ],
  },
  {
    title: 'AI Tools',
    sections: [
      {
        label: 'Intelligence',
        items: [
          { title: 'Knowledge Base', href: '/panel/knowledge-base', icon: BookOpen, description: 'Documents & knowledge' },
          { title: 'Global Knowledge', href: '/panel/global-knowledge', icon: Library, description: 'Shared firm knowledge', minPlan: 0 },
          { title: 'ATO Knowledge', href: '/panel/ato-knowledge', icon: BookOpen, description: 'ATO benchmarks & rulings' },
          { title: 'ASIC Knowledge', href: '/panel/asic-knowledge', icon: Shield, description: 'ASIC guides & compliance' },
          { title: 'Ask All Clients', href: '/panel/multi-client', icon: MessageSquare, minPlan: 3, description: 'Query all clients at once' },
        ],
      },
    ],
  },
  {
    title: 'Workflow',
    sections: [
      {
        label: 'Automation',
        items: [
          { title: 'Workflows', href: '/panel/compliance/workflows', icon: CheckCheck, minPlan: 3, description: 'Workflow templates' },
          { title: 'Reminders', href: '/panel/compliance/reminders', icon: AlertTriangle, minPlan: 2, description: 'Smart reminders' },
        ],
      },
    ],
  },
  {
    title: 'Automation',
    sections: [{
      label: 'Document Automation',
      items: [
        { title: 'Generate Document', href: '/panel/automation', icon: Sparkles, minPlan: 2, description: 'Create personalised client letters' },
        { title: 'Templates', href: '/panel/automation?tab=templates', icon: FileText, minPlan: 2, description: 'Manage letter templates' },
        { title: 'Letterheads', href: '/panel/automation?tab=letterheads', icon: Layers, minPlan: 2, description: 'Firm-branded letterhead templates' },
        { title: 'History', href: '/panel/automation?tab=history', icon: Clock, minPlan: 2, description: 'Generated documents history' },
      ],
    }],
  },
  {
    title: 'SMSF',
    columns: 3,
    sections: [
      {
        label: 'Overview',
        items: [
          { title: 'SMSF Dashboard', href: '/panel/smsf', icon: LayoutDashboard, minPlan: 3, description: 'Firm-wide SMSF overview' },
          { title: 'All Funds', href: '/panel/smsf/funds', icon: Shield, minPlan: 3, description: 'Fund list & admin' },
          { title: 'Register Fund', href: '/panel/smsf/new', icon: FileText, minPlan: 3, description: 'New fund setup wizard' },
        ],
      },
      {
        label: 'Management',
        items: [
          { title: 'Contributions & Caps', href: '/panel/smsf/contributions', icon: DollarSign, minPlan: 3, description: 'Cap tracking & calculators' },
          { title: 'Pensions & Benefits', href: '/panel/smsf/pensions', icon: Users, minPlan: 3, description: 'Pension payments & TBC' },
          { title: 'Investments', href: '/panel/smsf/investments', icon: Shield, minPlan: 3, description: 'Investment compliance' },
          { title: 'Reporting & Audit', href: '/panel/smsf/reporting', icon: FileText, minPlan: 3, description: 'SAR, statements & audit' },
        ],
      },
      {
        label: 'Tools',
        items: [
          { title: 'Compliance & Risk', href: '/panel/smsf/compliance', icon: ShieldAlert, minPlan: 3, description: 'Compliance checklist & risk' },
          { title: 'Advisory & Strategy', href: '/panel/smsf/advisory', icon: Zap, minPlan: 3, description: 'Contribution & estate strategy' },
          { title: 'Automation', href: '/panel/smsf/automation', icon: Settings, minPlan: 3, description: 'Feeds, templates & automation' },
          { title: 'Knowledge Base', href: '/panel/smsf/knowledge', icon: BookOpen, minPlan: 3, description: 'Rulings, education & alerts' },
          { title: 'Trustee Portal', href: '/panel/smsf/portal', icon: Globe, minPlan: 3, description: 'Client-facing portal preview' },
        ],
      },
    ],
  },
  {
    title: 'More',
    sections: [
      {
        label: 'Tools',
        items: [
          { title: 'Feature Marketplace', href: '/panel/features', icon: Sparkles, description: 'Browse all features' },
          { title: 'Help Center', href: '/panel/help', icon: HelpCircle, description: 'Guides and help' },
        ],
      },
      {
        label: 'Settings',
        items: [
          { title: 'Xero Integration', href: '/panel/integrations/xero', icon: Link2, description: 'Connect Xero' },
          { title: 'Organization', href: '/panel/organization', icon: Network, description: 'Firm structure' },
          { title: 'Users', href: '/panel/users', icon: Users, description: 'Manage users' },
          { title: 'Billing', href: '/panel/billing', icon: CreditCard, description: 'Subscription & billing' },
          { title: 'Settings', href: '/panel/settings', icon: Settings, description: 'App settings' },
        ],
      },
    ],
  },
];

// Ribbon: 15 most important features in a single row
const RIBBON_ITEMS: MenuItem[] = [
  { title: 'Dashboard', href: '/panel/firm/dashboard', icon: LayoutDashboard, minPlan: 2 },
  { title: 'Clients', href: '/panel/files/clients', icon: Users },
  { title: 'AI Chat', href: '/panel/kb-chat', icon: MessageSquare },
  { title: 'Deadlines', href: '/panel/deadlines', icon: CalendarDays, minPlan: 2 },
  { title: 'Compliance', href: '/panel/compliance/checker', icon: Shield, minPlan: 2 },
  { title: 'Audit Risk', href: '/panel/compliance/audit-risk', icon: ShieldAlert, minPlan: 3 },
  { title: 'Reports', href: '/panel/reports', icon: FileText, minPlan: 2 },
  { title: 'Reconciliation', href: '/panel/intelligence/reconciliation', icon: Scale, minPlan: 2 },
  { title: 'BAS Validator', href: '/panel/compliance/bas', icon: FileCheck, minPlan: 2 },
  { title: 'Tax Planning', href: '/panel/compliance/tax-planning', icon: CalendarDays, minPlan: 3 },
  { title: 'Opportunities', href: '/panel/compliance/opportunities', icon: Zap, minPlan: 3 },
  { title: 'Cash Flow', href: '/panel/compliance/cashflow', icon: BarChart3, minPlan: 3 },
  { title: 'Comparison', href: '/panel/intelligence/comparison', icon: GitCompare, minPlan: 2 },
  { title: 'Risk Warning', href: '/panel/intelligence/failure-warning', icon: AlertOctagon, minPlan: 3 },
];

// ==================== Component ====================

export function TopMenuBar({ onMenuClick, favourites }: { onMenuClick?: () => void; favourites?: string[] }) {
  const { hasPlanTier, isSuperAdmin } = useAuthStore();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasAccess = (item: MenuItem) => item.minPlan === undefined || hasPlanTier(item.minPlan);

  if (isSuperAdmin()) return null; // Super admin uses different layout

  return (
    <div ref={menuRef} className="bg-gradient-to-b from-background to-muted/20">
      {/* Top menu row - fixed to viewport top */}
      <div className="fixed top-0 left-0 right-0 flex items-center px-4 h-11 gap-1 border-b border-border/40 z-40 bg-background">
        <div className="flex items-center gap-1 flex-1 min-w-0 mr-[260px]">
        <Link to="/panel" className="flex items-center gap-2 mr-4 shrink-0">
          <img src={logoImg} alt="RoniDoc" className="h-7 w-auto" />
        </Link>

        {MENU_GROUPS.map(group => (
          <MenuDropdown
            key={group.title}
            group={group}
            isOpen={openMenu === group.title}
            onToggle={() => setOpenMenu(openMenu === group.title ? null : group.title)}
            onClose={() => setOpenMenu(null)}
            hasAccess={hasAccess}
          />
        ))}
        </div>
      </div>

      {/* Spacer to offset fixed top menu */}
      <div className="h-11" />

      {/* Ribbon with most-used features (scrolls with page) */}
      <div className="px-4 py-2 border-b border-border/40">
        <RibbonRow items={RIBBON_ITEMS} hasAccess={hasAccess} onItemClick={() => setOpenMenu(null)} />
      </div>
    </div>
  );
}

// ==================== Menu Dropdown ====================

function MenuDropdown({ group, isOpen, onToggle, onClose, hasAccess }: {
  group: MenuGroup;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  hasAccess: (item: MenuItem) => boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasActiveItem = group.sections.some(s => s.items.some(i => location.pathname.startsWith(i.href)));
  const cols = group.columns || 1;

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={cn(
          'px-3 h-8 rounded text-sm font-medium transition-colors flex items-center gap-1',
          isOpen ? 'bg-[#5048e5] text-white' :
          hasActiveItem ? 'bg-[#5048e5]/10 text-[#5048e5]' :
          'hover:bg-muted'
        )}
      >
        {group.title}
        <ChevronDown className={cn('h-3 w-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 mt-0.5 bg-popover border rounded-lg shadow-xl z-50 overflow-hidden',
            cols > 1 ? '' : 'min-w-[320px]',
          )}
          style={{ animationDuration: '150ms', ...(cols > 1 ? { width: cols * 260 } : {}) }}
        >
          <div className={cols > 1 ? `grid grid-cols-${cols}` : ''} style={cols > 1 ? { gridTemplateColumns: `repeat(${cols}, 1fr)` } : {}}>
            {group.sections.map((section, si) => (
              <div key={si} className={cn(cols > 1 ? 'border-r last:border-r-0' : si > 0 && 'border-t')}>
                {section.label && (
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </div>
                )}
                <div className="py-1">
                  {section.items.map(item => {
                    const hasItemAccess = hasAccess(item);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.href}
                        onClick={() => { if (hasItemAccess) { navigate(item.href); onClose(); } else { navigate('/panel/upgrade'); onClose(); } }}
                        className={cn(
                          'w-full flex items-start gap-3 px-3 py-2 hover:bg-muted/80 text-left transition-colors',
                          !hasItemAccess && 'opacity-60'
                        )}
                      >
                        <div className={cn(
                          'h-8 w-8 rounded flex items-center justify-center shrink-0',
                          hasItemAccess ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'bg-muted text-muted-foreground'
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            {item.title}
                            {!hasItemAccess && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Upgrade</span>}
                          </div>
                          {item.description && (
                            <p className="text-[11px] text-muted-foreground truncate">{item.description}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Ribbon ====================

function RibbonRow({ items, hasAccess, onItemClick }: { items: MenuItem[]; hasAccess: (item: MenuItem) => boolean; onItemClick?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden">
      {items.map(item => {
        const Icon = item.icon;
        const hasItemAccess = hasAccess(item);
        const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');

        return (
          <button
            key={item.href}
            onClick={() => { onItemClick?.(); if (hasItemAccess) navigate(item.href); else navigate('/panel/upgrade'); }}
            className={cn(
              'group flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md min-w-[72px] transition-all shrink-0 relative',
              isActive ? 'bg-[#5048e5]/15 text-[#5048e5]' :
              hasItemAccess ? 'hover:bg-muted text-foreground' :
              'hover:bg-muted/50 text-muted-foreground opacity-60'
            )}
            title={hasItemAccess ? item.title : `${item.title} (Upgrade required)`}
          >
            <div className={cn(
              'h-7 w-7 rounded-md flex items-center justify-center transition-all',
              isActive ? 'bg-[#5048e5] text-white' :
              hasItemAccess ? 'bg-gradient-to-br from-[#5048e5]/10 to-[#5048e5]/5 text-[#5048e5] group-hover:from-[#5048e5]/20 group-hover:to-[#5048e5]/10' :
              'bg-muted text-muted-foreground'
            )}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <span className="text-[10px] font-medium leading-tight whitespace-nowrap">{item.title}</span>
          </button>
        );
      })}
    </div>
  );
}
